import { useSyncExternalStore, useState, useEffect, useCallback, useMemo } from 'react';
import { createMachine } from '../createMachine';
import { useIsReady } from './useIsReady';
export function useCreateMachine(machineDefinition, options = {}) {
  const isReady = useIsReady(options);
  const [machine, setMachine] = useState(() => {
    return createMachine(machineDefinition, Object.assign({}, options, {
      waitToStart: true
    }));
  });
  const snapshot = useSyncExternalStore(useCallback(listener => machine.subscribe(listener), [machine]), () => machine.getSnapshot(), () => machine.getSnapshot());
  const state = useMemo(() => ({
    value: snapshot.value,
    context: snapshot.context,
    hasTag: tag => machine.hasTagForState(snapshot.value, tag),
    isRunning: () => machine.isRunning(),
    matches: name => snapshot.value === name,
    getInspector: () => machine.getInspector()
  }), [snapshot, machine]);
  useEffect(() => {
    const newMachine = createMachine(machineDefinition, Object.assign({}, options, {
      waitToStart: !isReady
    }));
    setMachine(newMachine);

    /**
     * We specifically only want to run this effect once, after the client initializes.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (machine.isRunning() || !isReady) {
      return;
    }
    machine.mergeContext(options.initialContext);
    machine.start();

    /**
     * We only want to listen to isReady and don't care if the machine changes. If we
     * transition to a ready state and the machine isn't already running we need to
     * update it with the latest context and then start it.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);
  const send = useCallback(event => {
    machine.send(event);
  }, [machine]);
  return {
    state,
    send
  };
}